import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import PanelFFXIV from '@/app/components/PanelFFXIV';
import { useTranslation } from '@/hooks/useTranslation';
import {
    TEAM_COLORS_CONFIG,
    TEAM_NAME_WORDS,
    getWordCategories,
    getTeamColorHex,
    TeamNameWordCategory,
    TeamColorConfig,
} from '@/lib/config/teams';
import { FaSave } from 'react-icons/fa';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { RxCross2 } from "react-icons/rx";
import { GiPerspectiveDiceSixFacesOne } from 'react-icons/gi';
import GameModeConfigSeparator from '../GameModes/GameModeConfigSeparator';
import ButtonFFXIV from '@/app/components/ButtonFFXIV';
import TooltipFFXIV from '@/app/components/TooltipFFXIV';
import { useAudio, AUDIO } from '@/app/providers/AudioProvider';


const SAVED_TEAMS_KEY = 'eorguessr_saved_teams';
const MAX_SAVED_TEAMS = 4;

interface SavedTeam {
    id: string;
    name: string;
    color: string;
}

function getSavedTeams(): SavedTeam[] {
    if (typeof window === 'undefined') return [];
    try {
        const saved = localStorage.getItem(SAVED_TEAMS_KEY);
        return saved ? JSON.parse(saved) : [];
    } catch {
        return [];
    }
}

function saveSavedTeams(teams: SavedTeam[]): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(SAVED_TEAMS_KEY, JSON.stringify(teams));
}

type ActiveWordSlot = 'first' | 'second' | null;

interface TeamSelectorProps {
    isOpen: boolean;
    currentColor: string;
    currentName: string;
    onSelect: (color: string, name: string) => void;
    onClose: () => void;
}

const TeamSelector: React.FC<TeamSelectorProps> = ({
    isOpen,
    currentColor,
    currentName,
    onSelect,
    onClose
}) => {
    const { t } = useTranslation();
    const { play } = useAudio();

    // Parse current name into first and second word
    const nameParts = currentName.split(' ');
    const initialFirstWord = nameParts[0] || '';
    const initialSecondWord = nameParts.slice(1).join(' ') || '';

    const [selectedColor, setSelectedColor] = useState(currentColor);
    const [selectedFirstWord, setSelectedFirstWord] = useState(initialFirstWord);
    const [selectedSecondWord, setSelectedSecondWord] = useState(initialSecondWord);
    const [selectedCategory, setSelectedCategory] = useState<TeamNameWordCategory | undefined>(undefined);
    const [savedTeams, setSavedTeams] = useState<SavedTeam[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeWordSlot, setActiveWordSlot] = useState<ActiveWordSlot>(null);

    // Load saved teams on mount
    useEffect(() => {
        setSavedTeams(getSavedTeams());
    }, [isOpen]);

    // Reset state when modal opens with new values
    useEffect(() => {
        if (isOpen) {
            const parts = currentName.split(' ');
            setSelectedColor(currentColor);
            setSelectedFirstWord(parts[0] || '');
            setSelectedSecondWord(parts.slice(1).join(' ') || '');
            setSearchQuery('');
            setActiveWordSlot(null);
            setSelectedCategory(undefined);
        }
    }, [isOpen, currentColor, currentName]);

    const wordCategories = useMemo(() => getWordCategories(), []);

    // Filter words by category and search query
    const filteredWords = useMemo(() => {
        let words = TEAM_NAME_WORDS;

        // Filter by category
        if (selectedCategory) {
            words = words.filter(w => w.category === selectedCategory);
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            words = words.filter(w => w.word.toLowerCase().includes(query));
        }

        return words;
    }, [selectedCategory, searchQuery]);

    // Handle word selection
    const handleWordClick = (word: string) => {
        if (activeWordSlot === 'first') {
            setSelectedFirstWord(word);
        } else if (activeWordSlot === 'second') {
            setSelectedSecondWord(word);
        }
    };

    if (!isOpen) return null;

    const teamPreviewName = `${selectedFirstWord} ${selectedSecondWord}`.trim() || currentName;
    const canSave = savedTeams.length < MAX_SAVED_TEAMS;

    const handleConfirm = () => {
        const newName = `${selectedFirstWord} ${selectedSecondWord}`.trim();
        onSelect(selectedColor, newName || currentName);
        onClose();
    };

    const handleSaveTeam = () => {
        if (!canSave) return;

        const newTeam: SavedTeam = {
            id: Date.now().toString(),
            name: teamPreviewName,
            color: selectedColor,
        };

        const updatedTeams = [...savedTeams, newTeam];
        setSavedTeams(updatedTeams);
        saveSavedTeams(updatedTeams);
    };

    const handleLoadTeam = (team: SavedTeam) => {
        const parts = team.name.split(' ');
        setSelectedColor(team.color);
        setSelectedFirstWord(parts[0] || '');
        setSelectedSecondWord(parts.slice(1).join(' ') || '');
    };

    const handleDeleteTeam = (teamId: string) => {
        const updatedTeams = savedTeams.filter(t => t.id !== teamId);
        setSavedTeams(updatedTeams);
        saveSavedTeams(updatedTeams);
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-75 z-[1000] flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                className="relative max-w-4xl max-h-[90vh] overflow-y-auto"
            >
                <PanelFFXIV className="!px-0 !pb-0 !pt-2 border-2 border-x-[#c0a270] border-y-[#e0c290] !w-[880px]">
                    <div className="text-xl mb-1 px-3">{t("teamSelector.title")}</div>
                    <GameModeConfigSeparator direction="horizontal" />
                    <div className="flex gap-3 px-3">
                        {/* Colors */}
                        <div className="flex flex-col gap-2 mt-2">
                            {TEAM_COLORS_CONFIG.map((color: TeamColorConfig) => (
                                <button
                                    key={color.id}
                                    onMouseEnter={() => play(AUDIO.HOVER)}
                                    onClick={() => { play(AUDIO.CLICK); setSelectedColor(color.id); }}
                                    className={`w-7 h-7 rounded border-x-[#c0a270] border-y-[#e0c290] shadow-[0_0_3px_#000,inset_0_0_4px_#000] ${selectedColor === color.id ? 'border-2' : ''}`}
                                    style={{ backgroundColor: color.hex }}
                                />
                            ))}
                        </div>
                        <GameModeConfigSeparator direction="vertical" />
                        {/* Words */}
                        <div className="relative flex flex-col gap-4 mt-2 grow">
                            {/* Current team name */}
                            <div className="flex gap-3 items-center justify-center">
                                <div
                                    className={`w-7 h-7 rounded border-2 border-x-[#c0a270] border-y-[#e0c290] shadow-[0_0_3px_#000,inset_0_0_4px_#000]`}
                                    style={{ backgroundColor: getTeamColorHex(selectedColor) }}
                                    title={selectedColor}
                                />
                                <div
                                    onMouseEnter={() => play(AUDIO.HOVER)}
                                    onClick={() => { play(AUDIO.CLICK); setActiveWordSlot('first'); }}
                                    className={`bg-[#4C4C4C] rounded-md shadow shadow-black/50 py-0.5 px-3 text-xl cursor-pointer w-[200px] transition-all ${activeWordSlot === 'first' ? 'ring-2 ring-[#e0c290]' : 'hover:ring-1 hover:ring-[#c0a270]/50'}`}
                                >
                                    {selectedFirstWord}
                                </div>
                                <div
                                    onMouseEnter={() => play(AUDIO.HOVER)}
                                    onClick={() => { play(AUDIO.CLICK); setActiveWordSlot('second'); }}
                                    className={`bg-[#4C4C4C] rounded-md shadow shadow-black/50 py-0.5 px-3 text-xl cursor-pointer w-[200px] transition-all ${activeWordSlot === 'second' ? 'ring-2 ring-[#e0c290]' : 'hover:ring-1 hover:ring-[#c0a270]/50'}`}
                                >
                                    {selectedSecondWord}
                                </div>
                                <div
                                    className="ffxiv-button text-center relative group rounded-full shadow-md shadow-black/30 cursor-pointer h-7 w-7 hover:shadow-lg"
                                    onMouseEnter={() => play(AUDIO.HOVER)}
                                    onClick={() => {
                                        play(AUDIO.CLICK);
                                        // Randomize color
                                        const randomColorIndex = Math.floor(Math.random() * TEAM_COLORS_CONFIG.length);
                                        setSelectedColor(TEAM_COLORS_CONFIG[randomColorIndex].id);
                                        // Randomize name words
                                        const word1 = TEAM_NAME_WORDS[Math.floor(Math.random() * TEAM_NAME_WORDS.length)];
                                        const word2 = TEAM_NAME_WORDS[Math.floor(Math.random() * TEAM_NAME_WORDS.length)];
                                        setSelectedFirstWord(word1.word);
                                        setSelectedSecondWord(word2.word);
                                    }}
                                >
                                    <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-full pointer-events-none"></div>
                                    <GiPerspectiveDiceSixFacesOne className="text-xl absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                                    <TooltipFFXIV><span className="text-white">{t("teamSelector.randomizeTeam")}</span></TooltipFFXIV>
                                </div>
                                <div
                                    className={`ffxiv-button text-center relative group rounded-full shadow-md shadow-black/30 cursor-pointer h-7 w-7 ${!canSave ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'}`}
                                    onMouseEnter={() => canSave && play(AUDIO.HOVER)}
                                    onClick={() => {
                                        if (canSave) {
                                            play(AUDIO.CLICK);
                                        }
                                        handleSaveTeam();
                                    }}
                                >
                                    <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-full pointer-events-none"></div>
                                    <FaSave className="text-md absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                                    <TooltipFFXIV><span className="text-white">{canSave ? t("teamSelector.saveCurrentConfig") : t("teamSelector.maxSavedReached")}</span></TooltipFFXIV>
                                </div>
                            </div>

                            {/* Saved preset */}
                            <div className="w-full">
                                <div className="flex items-center gap-2">
                                    <div className="uppercase text-2xl font-serif text-[#e0c290] text-shadow shadow-black/30">{t("teamSelector.savedPresets")}</div>
                                </div>
                                <div className="flex gap-5 items-center overflow-x-auto py-2">
                                    {savedTeams.map((team) => (
                                        <div
                                            key={team.id}
                                            onMouseEnter={() => play(AUDIO.HOVER)}
                                            onClick={() => { play(AUDIO.CLICK); handleLoadTeam(team); }}
                                            className="flex gap-2 items-center px-2 py-1 rounded bg-[#4C4C4C] shadow shadow-black/50 cursor-pointer hover:bg-[#5C5C5C]"
                                        >
                                            <div className="w-4 h-4 rounded border border-x-[#c0a270] border-y-[#e0c290] shadow-[0_0_3px_#000,inset_0_0_4px_#000]" style={{ backgroundColor: getTeamColorHex(team.color) }}></div>
                                            <span>{team.name}</span>
                                            <RxCross2
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (window.confirm(t("teamSelector.confirmDelete", { teamName: team.name }))) {
                                                        handleDeleteTeam(team.id);
                                                    }
                                                }}
                                                className="hover:text-red-500 rounded hover:bg-white/20"
                                            />
                                        </div>
                                    ))}
                                    {savedTeams.length === 0 && (
                                        <div className="text-gray-400 italic h-[26px]">{t("teamSelector.noSavedPresets")}</div>
                                    )}
                                </div>
                            </div>

                            {/* Word selection */}
                            <div className={`relative flex gap-4 transition-opacity ${activeWordSlot ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                                {/* Categories */}
                                <div className="flex flex-col gap-2 mb-2">
                                    <div className="uppercase text-2xl font-serif text-[#e0c290] text-shadow shadow-black/30">{t("teamSelector.category")}</div>
                                    <div
                                        onMouseEnter={() => play(AUDIO.HOVER)}
                                        onClick={() => { play(AUDIO.CLICK); setSelectedCategory(undefined); }}
                                        className={`relative group px-3 py-[2px] cursor-pointer`}
                                    >
                                        <div className={`absolute z-[0] rounded-full top-0 left-0 blur-[1px] w-full h-full bg-gradient-to-r from-orange-300 to-transparent opacity-0 ${selectedCategory === undefined ? 'opacity-30' : 'group-hover:opacity-30'}`}></div>
                                        <span className="relative z-[1] text-[19px] leading-[19px] text-shadow-border shadow-black/50">{t("teamNameCategories.all")}</span>
                                    </div>
                                    {wordCategories.map((cat) => (
                                        <div
                                            key={cat}
                                            onMouseEnter={() => play(AUDIO.HOVER)}
                                            onClick={() => { play(AUDIO.CLICK); setSelectedCategory(cat); }}
                                            className={`relative group px-3 py-[2px] cursor-pointer`}
                                        >
                                            <div className={`absolute z-[0] rounded-full top-0 left-0 blur-[1px] w-full h-full bg-gradient-to-r from-orange-300 to-transparent opacity-0 ${selectedCategory === cat ? 'opacity-30' : 'group-hover:opacity-30'}`}></div>
                                            <span className="relative z-[1] text-[19px] leading-[19px] text-shadow-border shadow-black/50">{t("teamNameCategories." + cat)}</span>
                                        </div>
                                    ))}
                                </div>
                                <GameModeConfigSeparator direction="vertical" />
                                {/* Word list */}
                                <div className="flex flex-col justify-between grow mb-2">
                                    <div className="flex flex-col gap-2">
                                        <div className="flex justify-between">
                                            <div className="uppercase text-2xl font-serif text-[#e0c290] text-shadow shadow-black/30">{t("teamSelector.words")}</div>
                                            <div className="flex items-center gap-2">
                                                <FaMagnifyingGlass />
                                                <input
                                                    className="bg-[#4C4C4C] rounded-md shadow shadow-black/50 py-0.5 px-3 cursor-text text-lg w-[200px] outline-none focus:ring-1 focus:ring-[#c0a270]"
                                                    value={searchQuery}
                                                    onClick={() => { play(AUDIO.CLICK) }}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    placeholder={t("teamSelector.searchPlaceholder")}
                                                />
                                            </div>
                                        </div>
                                        {/* List */}
                                        <div className="overflow-y-auto h-[630px] max-h-[630px]">
                                            <div className="grid grid-cols-3 gap-2 ">
                                                {filteredWords.map((wordObj) => (
                                                    <div
                                                        key={`${wordObj.category}-${wordObj.word}`}
                                                        onMouseEnter={() => { play(AUDIO.HOVER) }}
                                                        onClick={() => { play(AUDIO.CLICK); handleWordClick(wordObj.word); }}
                                                        className="w-[200px] relative group px-3 py-[2px] cursor-pointer">
                                                        <div className={`absolute z-[0] rounded-full top-0 left-0 blur-[1px] w-full h-full bg-gradient-to-r from-orange-300 to-transparent opacity-0 ${(activeWordSlot === 'first' && selectedFirstWord === wordObj.word) || (activeWordSlot === 'second' && selectedSecondWord === wordObj.word) ? 'opacity-30' : 'group-hover:opacity-30'}`}></div>
                                                        <span className="relative z-[1] text-[19px] leading-[19px] text-shadow-border shadow-black/50">{wordObj.word}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {!activeWordSlot && (
                                <PanelFFXIV className="!absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-fit z-20 border-2 border-x-[#c0a270] border-y-[#e0c290] !p-3">
                                    <div className="flex flex-col gap-1 text-lg justify-center items-center text-shadow-border shadow-black/50">
                                        <div>{t("teamSelector.selectBoxHint")}</div>
                                        <div>{t("teamSelector.searchHint")}</div>
                                    </div>
                                </PanelFFXIV>
                            )}
                        </div>
                    </div>
                    <div className="sticky bottom-0 bg-[#323232] pb-3 rounded-xl z-[1]">
                        <GameModeConfigSeparator direction="horizontal" />
                        <div className="sticky bottom-0 flex items-center justify-center gap-4 mt-3">
                            <ButtonFFXIV
                                onClick={handleConfirm}
                                text={t("teamSelector.confirm")}
                                fitContent={true}
                                className='!w-[150px]'
                            />
                            <ButtonFFXIV
                                onClick={onClose}
                                text={t("teamSelector.cancel")}
                                fitContent={true}
                                className='!w-[150px]'
                            />
                        </div>
                    </div>

                </PanelFFXIV>
            </motion.div>
        </div>
    );
};

export default TeamSelector;